/**
 * KJV/NASB Strong's alignment for the comparison strips.
 *
 * Runs LCS over the two Strong's-number sequences so each OCCURRENCE gets
 * a status — set membership against onlyInKjv/onlyInNasb would misclassify
 * a number that appears twice on one side and once on the other (John 1:18's
 * doubled G2316). Output is one column per aligned slot; a column missing
 * its KJV or NASB half renders as a ghost cell.
 */

import type { ScriptureWord } from '@skjv/common';

export interface KjvCell {
  /** English word(s) — unnumbered helper words fold into the neighbor cell */
  text: string;
  number: string;
  /**
   * Index of the KJV word this cell came from. Cells sharing it are ONE
   * English word carrying several Strong's numbers (1 John 4:15
   * "Whosoever" = Ὃς ἄν, G3739+G302) and render as a single container.
   */
  groupId: number;
}

export interface AlignedSlot {
  status: 'common' | 'kjv-only' | 'nasb-only';
  kjv?: KjvCell;
  nasb?: { number: string };
}

/**
 * Flatten verse words into one cell per Strong's occurrence — the LCS
 * needs a token per number, so a multi-number word still yields several
 * cells; they carry a shared groupId so the view can re-join them.
 * Words without numbers (italic helpers) fold into the previous word's
 * cells (leading ones fold into the first numbered cell). The helper
 * appends to EVERY cell of that word, so all cells of a group read
 * identically and the merged container can use any of them.
 */
export function buildKjvCells(words: ScriptureWord[]): KjvCell[] {
  const cells: KjvCell[] = [];
  let pendingPrefix = '';
  let openGroup: KjvCell[] = [];

  words.forEach((word, index) => {
    if (word.strongsNumbers.length === 0) {
      if (openGroup.length > 0) {
        for (const cell of openGroup) cell.text += ` ${word.word}`;
      } else {
        pendingPrefix += `${word.word} `;
      }
      return;
    }
    openGroup = word.strongsNumbers.map((number) => ({
      text: `${pendingPrefix}${word.word}`,
      number,
      groupId: index,
    }));
    pendingPrefix = '';
    cells.push(...openGroup);
  });

  return cells;
}

/**
 * Re-join consecutive slots whose KJV halves came from one English word,
 * so "Whosoever" renders once over G3739+G302 instead of repeating on
 * every cell. A slot with no KJV half (NASB-only) breaks the run — if
 * the critical text inserts between two numbers of the same word, that
 * word falls back to separate columns rather than mis-spanning.
 */
export function groupSlots(slots: AlignedSlot[]): AlignedSlot[][] {
  const groups: AlignedSlot[][] = [];

  for (const slot of slots) {
    const open = groups[groups.length - 1];
    const openId = open?.[open.length - 1]?.kjv?.groupId;
    if (open && slot.kjv && openId !== undefined && openId === slot.kjv.groupId) {
      open.push(slot);
    } else {
      groups.push([slot]);
    }
  }

  return groups;
}

/** Align KJV cells against the NASB number sequence into display columns */
export function alignSequences(kjvCells: KjvCell[], nasbNumbers: string[]): AlignedSlot[] {
  const a = kjvCells.map((c) => c.number);
  const b = nasbNumbers;

  const dp: number[][] = Array.from({ length: a.length + 1 }, () =>
    new Array<number>(b.length + 1).fill(0),
  );
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const slots: AlignedSlot[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length || j < b.length) {
    if (i < a.length && j < b.length && a[i] === b[j]) {
      slots.push({ status: 'common', kjv: kjvCells[i], nasb: { number: b[j] } });
      i++;
      j++;
    } else if (j >= b.length || (i < a.length && dp[i + 1][j] >= dp[i][j + 1])) {
      slots.push({ status: 'kjv-only', kjv: kjvCells[i] });
      i++;
    } else {
      slots.push({ status: 'nasb-only', nasb: { number: b[j] } });
      j++;
    }
  }
  return slots;
}
